import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Company } from 'src/pages/billing/types/billing.types';
import { Check, Lightbulb } from 'lucide-react';
import { fuzzyFilterSimilarity } from 'src/services/fuzzy-match';

interface BorderedCompanySelectProps {
  companies: Array<Company>;
  value: string; // companyId
  onChange: (companyId: string) => void;
  label?: string;
  error?: string;
  className?: string;
  textClassName?: string;
  // Suggestion props
  suggestionQuery?: string; // Used for fuzzy matching suggestions (e.g., accountName)
  campaignAccountCompanyId?: string; // If provided, suggests this company first
  attentionNeeded?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
}

const BorderedCompanySelect: React.FC<BorderedCompanySelectProps> = ({
  companies,
  value,
  onChange,
  label,
  error,
  className,
  textClassName,
  suggestionQuery,
  campaignAccountCompanyId,
  attentionNeeded = false,
  isLoading = false,
  disabled = false,
}) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>(companies);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Calculate dropdown position for portal
  useEffect(() => {
    if (showDropdown && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);

  // Smart suggestion logic based on name matching
  const suggestedCompanies = useMemo(() => {
    if (companies.length === 0) return [];
    if (campaignAccountCompanyId) {
      const company = companies.find(c => c.id === campaignAccountCompanyId);
      if (company) {
        return [company];
      }
    }
    if (suggestionQuery) {
      const results = fuzzyFilterSimilarity(
        companies,
        suggestionQuery,
        (company: Company) => company.company_name,
        { limit: 3, threshold: 20 }
      );
      return results.map((result) => result.item).slice(0, 1);
    }
    return [];
  }, [companies, suggestionQuery, campaignAccountCompanyId]);

  const handleCompanySelect = (selectedCompanyId: string) => {
    onChange(selectedCompanyId);
    setShowDropdown(false);
  };

  const handleDropdownToggle = () => {
    if (!disabled && !isLoading) {
      setShowDropdown(!showDropdown);
    }
  };

  const handleSearch = (searchQuery: string) => {
    setSearchQuery(searchQuery);
    setFilteredCompanies(
      companies.filter(company =>
        company.company_name &&
        company.company_name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  };

  const selectedCompany = companies.find(c => c.id === value);
  const displayText = selectedCompany?.company_name || 'Unassigned';

  // Sort companies alphabetically and filter out suggested companies
  const sortedCompanies = useMemo(() => {
    const suggestedIds = new Set(suggestedCompanies.map(c => c.id));
    const filtered = filteredCompanies.filter(company => !suggestedIds.has(company.id));
    return filtered.sort((a, b) => {
      const nameA = a.company_name || '';
      const nameB = b.company_name || '';
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base', numeric: true });
    });
  }, [filteredCompanies, suggestedCompanies]);

  const dropdownContent = showDropdown && dropdownPosition && (
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${Math.max(dropdownPosition.width, 320)}px`, // Minimum 320px width for more substantial feel
        minWidth: '320px',
      }}
    >
      <div className="p-1">
        <div className="px-3 py-2 text-xs text-gray-500 font-medium border-b border-gray-100">
          <input
            type="text"
            placeholder="Search companies"
            className="w-full text-sm text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            autoFocus
          />
        </div>
        {/* Verify option */}
        {attentionNeeded && value && selectedCompany && (
          <>
            <div className="px-3 text-xs text-gray-500">
              Verify
            </div>
            <button
              onClick={() => handleCompanySelect(value)}
              className="group w-full text-left px-3 py-2 hover:bg-green-50 rounded text-sm transition-colors duration-150 flex items-center gap-2 bg-green-50/50 border-b border-green-100 hover:bg-green-500"
            >
              <Check className="size-3 flex-shrink-0 text-green-600 group-hover:text-white transition-colors duration-150" />
              <span className="truncate font-medium text-gray-800 group-hover:text-white transition-colors duration-150">
                {selectedCompany.company_name}
              </span>
            </button>
          </>
        )}
        {/* Suggested companies at the top */}
        {!value && suggestedCompanies && suggestedCompanies.length > 0 && (
          <>
            <div className="px-3 text-xs text-gray-500">
              Suggestions
            </div>
            {suggestedCompanies.map((company) => (
              <button
                key={company.id}
                onClick={() => handleCompanySelect(company.id)}
                className="group w-full text-left px-3 py-2 hover:bg-yellow-50 rounded text-sm transition-colors duration-150 flex items-center gap-2 bg-yellow-50/50 border-b border-yellow-100 hover:bg-yellow-500"
              >
                <Lightbulb className="size-3 flex-shrink-0 text-yellow-600 group-hover:text-white transition-colors duration-150" />
                <span className="truncate font-medium text-gray-800 group-hover:text-white transition-colors duration-150">
                  {company.company_name}
                </span>
              </button>
            ))}
            {/* {sortedCompanies.length > 0 && (
              <div className="border-t border-gray-200 my-1" />
            )} */}
          </>
        )}
        {/* Regular companies list */}
        {sortedCompanies.length === 0 && (!suggestedCompanies || suggestedCompanies.length === 0) ? (
          <div className="px-3 py-2 text-sm text-gray-500 text-center">
            No companies available
          </div>
        ) : (
          sortedCompanies.map((company) => (
            <button
              key={company.id}
              onClick={() => handleCompanySelect(company.id)}
              className={`w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 flex items-center gap-2 ${
                value === company.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
              }`}
            >
              {/* <Building2 className="size-3 flex-shrink-0" /> */}
              <span className="truncate">
                {company.company_name}
              </span>
              {value === company.id && (
                <div className="ml-auto">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                </div>
              )}
            </button>
          ))
        )}
        {value && (
          <>
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                onClick={() => handleCompanySelect('')}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 text-gray-500 flex items-center gap-2"
              >
                <div className="size-3 flex-shrink-0 border border-gray-300 rounded" />
                <span>
                  Unassign Company
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className="flex flex-col">
        <div
          className={`group relative rounded cursor-pointer ${error ? 'border-red-500' : ''} ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${className || ''}`}
          style={{
            paddingLeft: '1px',
            paddingRight: '5px',
            display: 'inline-block',
            maxWidth: '250px',
            maxHeight: 36,
          }}
        >
          {label && (
            <label className="absolute -top-2 left-3 mb-1 text-xs font-light text-gray-500 z-10 pointer-events-none">
              {label}
            </label>
          )}
          <div
            ref={triggerRef}
            onClick={handleDropdownToggle}
            className={`
              appearance-none bg-transparent w-full px-3 py-2 rounded transition-all duration-300
              border-1 border-transparent 
              group-hover:border-gray-500 
              group-hover:border 
              focus:outline-none
              flex items-center justify-between
              ${error ? 'group-hover:border-red-500 group-focus-within:border-red-500 border border-red-500' : ''}
              ${disabled || isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}
            `}
            style={{
              maxHeight: 36,
            }}
          >
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* <Building2
                className={`size-4 flex-shrink-0 ${attentionNeeded ? 'text-red-500' : 'text-gray-500'}`}
              /> */}
              <span
                className={`text-sm truncate ${textClassName || 'text-gray-500'} ${attentionNeeded ? 'text-red-500 font-bold' : ''}`}
                title={displayText}
              >
                {isLoading ? 'Updating...' : displayText}
              </span>
            </div>
            {/* <ChevronDown className={`size-4 flex-shrink-0 text-gray-500 ${disabled || isLoading ? '' : 'cursor-pointer'}`} /> */}
          </div>
        </div>
        {error && (
          <span className="mt-1 text-xs text-red-500">
            {error}
          </span>
        )}
      </div>
      {typeof document !== 'undefined' && createPortal(dropdownContent, document.body)}
    </>
  );
};

export default BorderedCompanySelect;

